// 분기 슬롯 condition(evaluateCondition 화이트리스트 문법) ↔ 구조화 모델(ConditionModel) 상호 변환.
//
// 목적: 슬롯 조건을 일반 사용자가 콤보박스로 구성하는 조건 빌더의 순수 코어. 빌더는 이 모델을
// 편집하고 serializeCondition으로 문자열을 만들어 기존 updateBranchSlotCondition op에 넘긴다
// (스키마·엔진 무변경). 로드 시 기존 condition을 parseCondition으로 모델화한다.
//
// 화이트리스트 문법(evaluateCondition, packages/report-composer/src/RenderTemplate.tsx와 동일):
//   - key                    → truthy(값 있음)
//   - !key                   → falsy(값 없음)
//   - key === '리터럴'       → eq(같음)
//   - key !== '리터럴'       → neq(다름)
//   - A && B                 → and 결합(&&가 || 보다 먼저 결합)
//   - A || B                 → or 결합
//   괄호·중첩은 미지원(evaluateCondition과 동일). 리터럴은 작은따옴표 안 '([^']*)'만.
//
// 모델은 좌→우 선형(flat) 표현이다. clauses[0]은 joiner 없음, 이후 각 절은 앞 결합자(and/or)를
// 가진다. && 는 'and', || 는 'or'. 직렬화는 이 선형 순서를 그대로 && / || 로 이어 붙인다(precedence는
// 문자열 형태에서 &&가 먼저 결합하는 것으로 자연 보존 — 빌더는 선형 체인만 생성).

import { CONDITION_CLAUSE_RE, CONDITION_IDENT_RE } from '@safetyhub/report-composer';

export type ConditionOp = 'eq' | 'neq' | 'truthy' | 'falsy';
export type ConditionJoiner = 'and' | 'or';

/** 조건 1절. 첫 절은 joiner 없음(undefined), 이후 절은 앞 결합자 보유. */
export interface ConditionClause {
  joiner?: ConditionJoiner;
  key: string;
  op: ConditionOp;
  /** op이 eq/neq일 때만 의미(truthy/falsy는 미사용). */
  value?: string;
}

export interface ConditionModel {
  clauses: ConditionClause[];
}

/** 값(리터럴)을 갖는 연산자인지. */
export function opTakesValue(op: ConditionOp): boolean {
  return op === 'eq' || op === 'neq';
}

/**
 * 절이 직렬화 가능한 "완성" 절인지. key가 비었거나, 값 연산자(eq/neq)인데 값이 비어 있으면
 * 미완성으로 본다. 미완성 절은 serializeCondition에서 제외되어 저장값(condition 문자열)에
 * 포함되지 않는다 — 값을 아직 입력하지 않은 eq 절이 `key === ''` 같은 의도치 않은 식으로
 * 저장되는 것을 방지한다(빌더 UI는 값을 채울 때까지 절을 로컬 드래프트로 화면에 유지).
 */
export function isCompleteClause(c: ConditionClause): boolean {
  if (c.key.trim() === '') return false;
  if (opTakesValue(c.op)) return (c.value ?? '') !== '';
  return true;
}

/**
 * enum 값 입력의 초기 모드 결정(하이브리드 콤보/직접 입력 빌더용, 순수 판정).
 *
 * 값이 비어 있지 않은데 옵션 목록의 value 어디에도 없으면 직접 입력 모드로 시작해야 한다
 * (콤보 select가 목록 밖 값을 표현하지 못해 값을 삼키는 것을 방지). 빈 값 또는 목록에 포함된
 * 값이면 콤보 모드(false)로 시작한다.
 */
export function shouldStartDirectValueInput(
  value: string,
  options: readonly { value: string }[],
): boolean {
  if (value === '') return false;
  return !options.some((o) => o.value === value);
}

/** 단일 절 문자열 → 절 모델(joiner 제외). 파싱 불가 시 null. */
function parseClause(raw: string): Omit<ConditionClause, 'joiner'> | null {
  const s = raw.trim();
  if (s === '') return null;

  // 정규식은 렌더 엔진 evaluateCondition과 공유(rc export) — 파싱↔평가 화이트리스트 단일 소스.
  const eq = CONDITION_CLAUSE_RE.exec(s);
  if (eq) {
    return { key: eq[1], op: eq[2] === '===' ? 'eq' : 'neq', value: eq[3] };
  }

  const negated = s.startsWith('!');
  const key = negated ? s.slice(1).trim() : s;
  if (!CONDITION_IDENT_RE.test(key)) return null;
  return { key, op: negated ? 'falsy' : 'truthy' };
}

/**
 * 조건식을 evaluateCondition과 같은 순서(|| → &&)로 절 단위 분해 — 절 원문 + 앞 결합자.
 * 새 || 그룹의 첫 절은 joiner 'or', 그 외 && 로 이어진 절은 'and', 전체 첫 절은 joiner 없음.
 *
 * parseCondition(모델화)과 collectUnevaluableClauses(경고 수집)의 공통 분해기 — 분해 규칙이
 * 두 벌로 갈라지면 "빌더가 읽는 절"과 "경고가 세는 절"이 어긋난다(단일 소스 유지).
 */
function splitClauses(trimmed: string): { raw: string; joiner?: ConditionJoiner }[] {
  const out: { raw: string; joiner?: ConditionJoiner }[] = [];
  const orGroups = trimmed.split('||');
  for (let gi = 0; gi < orGroups.length; gi += 1) {
    const andParts = orGroups[gi].split('&&');
    for (let ai = 0; ai < andParts.length; ai += 1) {
      let joiner: ConditionJoiner | undefined;
      if (gi === 0 && ai === 0) joiner = undefined;
      else if (ai === 0) joiner = 'or';
      else joiner = 'and';
      out.push({ raw: andParts[ai], joiner });
    }
  }
  return out;
}

/**
 * condition 문자열 → 모델. 빈 문자열/undefined는 clauses:[](else, 유효). 화이트리스트 문법을
 * 벗어난 절이 하나라도 있으면 null(빌더 진입 불가 → 직접 입력 모드).
 */
export function parseCondition(expr: string | undefined): ConditionModel | null {
  if (expr === undefined) return { clauses: [] };
  const trimmed = expr.trim();
  if (trimmed === '') return { clauses: [] };

  const clauses: ConditionClause[] = [];
  for (const { raw, joiner } of splitClauses(trimmed)) {
    const clause = parseClause(raw);
    if (!clause) return null;
    clauses.push({ ...clause, joiner });
  }
  return { clauses };
}

/**
 * 평가할 수 없는 절(원문)을 순서대로 수집 — 중복 제거. 정상식·빈 식이면 빈 배열.
 *
 * evaluateCondition은 화이트리스트를 벗어난 절을 **조용히 true**로 평가한다("미지원은 렌더"
 * 보수 원칙). 그래서 하이픈 식별자(`foo-bar`)처럼 흔한 오타는 에러 없이 그 절만 사라진 것과
 * 같아지고, 조건이 의도보다 느슨해진다(참조: memory/feedback-condition-ident-no-hyphen-silent-true).
 * 저장을 막지는 않으므로 편집기가 이 목록으로 경고만 띄운다.
 *
 * 판정은 parseClause(= 빌더가 쓰는 것과 같은 화이트리스트)에 위임한다. eq/neq 완성 절은
 * CONDITION_CLAUSE_RE가 식별자를 이미 강제하므로 자연히 통과한다. 빈 절(선행/후행 결합자)은
 * evaluateCondition도 true로 두는 무해한 형태라 경고 대상이 아니다.
 */
export function collectUnevaluableClauses(expr: string | undefined): string[] {
  if (expr === undefined) return [];
  const trimmed = expr.trim();
  if (trimmed === '') return [];

  const out: string[] = [];
  for (const { raw } of splitClauses(trimmed)) {
    const clause = raw.trim();
    if (clause === '') continue;
    if (parseClause(clause) !== null) continue;
    if (!out.includes(clause)) out.push(clause);
  }
  return out;
}

/**
 * 조건식에서 eq(===) 절만 뽑아 `{ [key]: value }` 맵으로 반환한다(분기 슬롯 시뮬레이션용).
 *
 * "그 슬롯 조건에 맞는 데이터가 들어온 것처럼" 미리보기하려면, 슬롯 조건의 동등 비교(예:
 * `matrixType === '5x5'`)를 데이터 소스 필터로 승격해야 한다. 이 함수는 그 승격 후보(eq 절의
 * key→value)만 순수하게 추출한다. truthy/falsy/neq 절은 특정 값으로 대체할 수 없어 제외한다.
 * 파싱 불가(화이트리스트 문법 이탈)면 빈 맵. 값이 빈 문자열인 미완성 eq 절도 제외한다.
 * 같은 key가 여러 eq로 나오면 뒤 절이 우선(last-wins) — 선형 체인 순서 그대로.
 */
export function extractEqClauses(expr: string | undefined): Record<string, string> {
  const model = parseCondition(expr);
  if (!model) return {};
  const out: Record<string, string> = {};
  for (const c of model.clauses) {
    if (c.op !== 'eq') continue;
    if (c.key.trim() === '') continue;
    const value = c.value ?? '';
    if (value === '') continue;
    out[c.key] = value;
  }
  return out;
}

/** 단일 절 모델 → 문자열. */
function clauseToString(c: ConditionClause): string {
  switch (c.op) {
    case 'eq':
      return `${c.key} === '${c.value ?? ''}'`;
    case 'neq':
      return `${c.key} !== '${c.value ?? ''}'`;
    case 'truthy':
      return c.key;
    case 'falsy':
      return `!${c.key}`;
    default:
      return c.key;
  }
}

/**
 * 모델 → condition 문자열. 완성 절(isCompleteClause)만 직렬화하고 미완성 절(값 미입력 eq/neq,
 * 빈 key)은 제외한다 — 편집 중 값을 채우지 않은 절이 `key === ''` 같은 식으로 저장되지 않게 한다.
 * 완성 절이 하나도 없으면 ''(else). 필터 후 첫 절은 결합자 없이, 이후 절은 joiner(and→' && ',
 * or→' || ')를 앞에 붙여 좌→우로 잇는다(미완성 절이 앞에서 빠지면 뒤 절의 결합자도 자연히 제거).
 */
export function serializeCondition(model: ConditionModel): string {
  return model.clauses
    .filter(isCompleteClause)
    .map((c, i) => {
      const clauseStr = clauseToString(c);
      if (i === 0) return clauseStr;
      const joiner = c.joiner === 'or' ? ' || ' : ' && ';
      return `${joiner}${clauseStr}`;
    })
    .join('');
}
